import { Box, Card, CardContent, Chip, Typography } from '@mui/material';
import DynamicIcon, { IconName } from '@shared/components/Icon';
import { theme } from '@shared/theme';
import React from 'react';

interface ReviewReaderBasicQuestionProps {
  label: string;
  answer: string | string[];
  iconGroup?: string;
}

function getIconForLabel(iconGroup?: string) {
  if (!iconGroup) {
    return IconName.Info;
  }
  if (iconGroup) {
    switch (iconGroup) {
      case '100':
        return IconName.Money; // Financial values
      case '200':
        return IconName.AccessTime; // Duration, time
      case '300':
        return IconName.Gavel; // RFP, approval process
      case '400':
        return IconName.CalendarToday; // Dates, timelines
      case '500':
        return IconName.EventNote; // Contracts, agreements
      case '600':
        return IconName.Person; // People-related fields
      default:
        return IconName.Info;
    }
  }
}

function ReviewReaderBasicQuestion({ label, answer, iconGroup }: ReviewReaderBasicQuestionProps) {
  const isArrayAnswer = Array.isArray(answer);

  return (
    <Card
      sx={{
        borderRadius: theme.spacing(1.25),
        boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.15)',
        background: '#f9fbfd',
        textAlign: 'center',
        padding: theme.spacing(1.5),
        transition: 'all 0.2s ease-in-out',
        height: 140,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        '&:hover': {
          boxShadow: '0px 6px 12px rgba(0, 0, 0, 0.2)',
        },
        [theme.breakpoints.down('md')]: {
          padding: theme.spacing(0.5),
          height: 160,
        },
      }}
    >
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: theme.spacing(1),
          }}
        >
          <DynamicIcon iconName={getIconForLabel(iconGroup) || IconName.Info} usePrimaryColor />
        </Box>
        <Typography
          sx={{
            fontWeight: 'bold',
            fontSize: 14,
            color: theme.palette.primary.main,
            marginBottom: theme.spacing(0.75),
            [theme.breakpoints.down('md')]: {
              fontSize: 12,
              marginBottom: theme.spacing(0.5),
            },
          }}
        >
          {label}
        </Typography>
        {isArrayAnswer ? (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: theme.spacing(0.5),
              justifyContent: 'center',
              alignItems: 'center',
              maxWidth: '100%',
            }}
          >
            {answer.map((item, index) => (
              <Chip
                key={index}
                label={item}
                size="small"
                variant="outlined"
                sx={{
                  fontSize: 12,
                  height: 24,
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: 'rgba(43, 144, 227, 0.1)',
                  },
                }}
              />
            ))}
          </Box>
        ) : (
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: 16,
              color: theme.palette.text.primary,
              [theme.breakpoints.down('md')]: {
                fontSize: 14,
              },
            }}
          >
            {answer || 'N/A'}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default ReviewReaderBasicQuestion;
